import React, { FC, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { checkoutCountryList as countryList } from 'shared/data/siteSettings';
import Button from 'components/commercetools-ui/atoms/button';
import Checkbox from 'components/commercetools-ui/atoms/checkbox';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Input from 'components/commercetools-ui/atoms/input';
import PasswordInput from 'components/commercetools-ui/atoms/input-password';
import Message from 'components/commercetools-ui/atoms/message';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { DataLayerOnLoad } from 'helpers/dataLayerHelpers';
import useLocale from 'helpers/hooks/useLocale';
import useValidate from 'helpers/hooks/useValidate';
import Redirect from 'helpers/redirect';
import { Reference } from 'types/reference';
import { useAccount, useCart } from 'frontastic';
interface Props {
  termsOfUseLink?: Reference;
}

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

const RegisterForm: FC<Props> = ({ ...props }) => {
  const { formatMessage: formatErrorMessage } = useFormat({ name: 'error' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { formatMessage } = useFormat({ name: 'common' });
  const [formStep, setFormStep] = useState('CHECK_EMAIL');
  const [selectedTitle, setSelectedTitle] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { validatePassword } = useValidate();
  const { register, loggedIn, pullAndRegister, checkCustomer, triggerVerificationEmail, login, account } = useAccount();
  const { data: cartData } = useCart();
  const { getCountry, getCurrency } = useLocale();
  const router = useRouter();
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState({
    message: '',
    type: '',
  });
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [dob, setDob] = useState({
    day: '',
    month: '',
    year: '',
  });
  const { validatePostalCode, validatePhoneNumeric, validateEmail, validateTextExists } = useValidate();

  const [marketingPreferences, setMarketingPreferences] = useState({
    email: false,
    post: false,
    sms: false,
    phone: false,
  });

  const [address, setAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    postcode: '',
    county: '',
    country: getCountry(),
  });

  const countryDropdownList =
    countryList.length > 0
      ? [...countryList.map(({ countryCode, country }) => ({ label: country, value: countryCode }))]
      : [];

  const [mobile, setMobile] = useState('');

  const searchParams = useSearchParams();
  const lvp = searchParams.get('lvp');

  //handle text input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value.trim() });
  };

  //data validation
  const validate = () => {
    if (!data.email && !validateEmail(data.email)) {
      setError({
        message: formatErrorMessage({ id: 'email.required', defaultMessage: 'Email is required.' }),
        type: 'error',
      });
      return false;
    }

    if (!validateTextExists(data.firstName)) {
      setError({
        message: formatErrorMessage({ id: 'firstName.required', defaultMessage: 'First name is required.' }),
        type: 'error',
      });
      return false;
    }

    if (!validateTextExists(data.lastName)) {
      setError({
        message: formatErrorMessage({ id: 'lastName.required', defaultMessage: 'Last name is required.' }),
        type: 'error',
      });
      return false;
    }

    if (!validatePassword(data.password)) {
      setError({
        message: formatErrorMessage({
          id: 'password.not.valid',
          defaultMessage: 'Password has to be at least 8 characters long and have at least one uppercase letter.',
        }),
        type: 'error',
      });
      return false;
    }

    if (data.password !== confirmPassword) {
      setError({
        message: formatErrorMessage({ id: 'password.mismatch', defaultMessage: 'Passwords must match.' }),
        type: 'error',
      });
      return false;
    }

    if (!validateTextExists(address.line1)) {
      setError({
        message: formatErrorMessage({ id: 'line1.required', defaultMessage: 'Address line 1 is required.' }),
        type: 'error',
      });
      return false;
    }
    if (!validateTextExists(address.city)) {
      setError({
        message: formatErrorMessage({ id: 'city.required', defaultMessage: 'City is required.' }),
        type: 'error',
      });
      return false;
    }

    if (!validateTextExists(address.postcode)) {
      setError({
        message: formatErrorMessage({ id: 'postcode.required', defaultMessage: 'Postcode is required.' }),
        type: 'error',
      });
      return false;
    }

    if (!validateTextExists(address.country)) {
      setError({
        message: formatErrorMessage({ id: 'country.required', defaultMessage: 'Country is required.' }),
        type: 'error',
      });
      return false;
    }

    if (!validateTextExists(mobile)) {
      setError({
        message: formatErrorMessage({ id: 'phone.required', defaultMessage: 'Mobile is required.' }),
        type: 'error',
      });
      return false;
    }

    if (!validatePostalCode(address.postcode, address.country)) {
      setError({
        message: formatErrorMessage({ id: 'postcode.invalid', defaultMessage: 'Please provide a valid postcode.' }),
        type: 'error',
      });
      return false;
    }

    if (mobile && !validatePhoneNumeric(mobile)) {
      setError({
        message: formatErrorMessage({
          id: 'mobile.invalid',
          defaultMessage: 'Please provide a valid mobile number.',
        }),
        type: 'error',
      });
      return false;
    }

    if (marketingPreferences.sms && !validateTextExists(mobile)) {
      setError({
        message: formatErrorMessage({ id: 'phone.required', defaultMessage: 'Mobile is required.' }),
        type: 'error',
      });
      return false;
    }

    if (marketingPreferences.sms && mobile && !validatePhoneNumeric(mobile)) {
      setError({
        message: formatErrorMessage({ id: 'mobile.invalid', defaultMessage: 'Please provide a valid mobile number.' }),
        type: 'error',
      });
      return false;
    }

    // Reset error if all validations pass
    setError({
      message: '',
      type: '',
    });
    return true;
  };

  const handleEmailCheck = async () => {
    setLoading(true);
    setError({
      message: '',
      type: '',
    });
    try {
      const { canCustomerPulled, isCustomerExistInCT, confirmed } = await checkCustomer(data);
      setLoading(false);

      if (isCustomerExistInCT) {
        if (confirmed) {
          setError({
            message: formatErrorMessage({
              id: 'account.exists',
              defaultMessage: 'An account with this email already exists. Please sign in.',
            }),
            type: 'info',
          });
        } else {
          triggerVerificationEmail(data);
          setError({
            message: formatErrorMessage({
              id: 'account.verify',
              defaultMessage:
                'An account with this email already exists. Please verify your account to be able to sign-in.',
            }),
            type: 'info',
          });
        }
      } else if (canCustomerPulled) {
        setFormStep('SET_PASSWORD');
      } else {
        setFormStep('REGISTER');
      }
    } catch (err) {
      setLoading(false);
      setError({
        message: formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }),
        type: 'error',
      });
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateEmail(data.email)) {
      setError({
        message: formatErrorMessage({ id: 'email.required', defaultMessage: 'Email is required.' }),
        type: 'error',
      });
      return;
    }
    await handleEmailCheck();
  };

  const handlePullAndRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateEmail(data.email)) {
      setError({
        message: formatErrorMessage({ id: 'email.required', defaultMessage: 'Email is required.' }),
        type: 'error',
      });
      return;
    }
    if (!validatePassword(data.password)) {
      setError({
        message: formatErrorMessage({
          id: 'password.not.valid',
          defaultMessage: 'Password has to be at least 8 characters long and have at least one uppercase letter.',
        }),
        type: 'error',
      });
      return;
    }
    setLoading(true);
    const response = (await pullAndRegister({ email: data.email, password: data.password })) as any;

    if (!response.account.accountId) {
      setError({
        message: formatErrorMessage({
          id: 'account.create.fail',
          defaultMessage: "Sorry. We couldn't create your account..",
        }),
        type: 'error',
      });
      setSuccess('');
    } else {
      setError({
        message: formatErrorMessage({
          id: 'account.create.success',
          defaultMessage: 'Successfully Registered!',
        }),
        type: 'success',
      });
      //await login(data.email, data.password);
      setTimeout(() => {
        if (!loggedIn) {
          router.push('/login');
        }
      }, 200);
    }
    setLoading(false);
  };

  const handleMarketingPreferenceChange = (e: { name: string; checked: boolean }) => {
    const { name, checked } = e;
    setMarketingPreferences((prev) => ({ ...prev, [name]: checked }));
  };

  const handleAddressChange = (e: any) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value.trim() }));
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMobile(e.target.value.trim());
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTitle(e.target.value);
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleDobChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setDob((prevDob) => ({ ...prevDob, [name]: value }));
  };

  useEffect(() => {
    setSelectedTitle('Mr');
  }, []);

  useEffect(() => {
    if (error) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [error]);

  useEffect(() => {
    const localeData = {
      country: getCountry(),
      currency: getCurrency(),
    };

    const currentUrl = document.location.origin;
    const dataLayer = new DataLayerOnLoad(currentUrl, 'registration', localeData, account);
    dataLayer.setDataLayerOnLoad();
  }, []);

  //form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validateResult = await validate();
    if (!validateResult) {
      return;
    }
    const shippingData = {
      shippingAddress: {
        title: selectedTitle,
        firstName: data.firstName,
        lastName: data.lastName,
        streetName: address.line1,
        additionalAddressInfo: address.line2,
        city: address.city,
        postalCode: address.postcode,
        county: address.county,
        country: address.country,
        phone: mobile,
      },
    };

    const birthday =
      dob.day && dob.month && dob.year
        ? {
            birthday: `${dob.year}-${dob.month}-${dob.day}`,
          }
        : {};

    const _phone = mobile
      ? {
          phone: mobile,
        }
      : {};

    const registrationData = {
      title: selectedTitle,
      ...data,
      isMailSubscribed: marketingPreferences.post,
      isEmailSubscribed: marketingPreferences.email,
      isSMSSubscribed: marketingPreferences.sms,
      isPhoneSubscribed: marketingPreferences.phone,
      ...shippingData,
      ...birthday,
      ..._phone,
    };

    setLoading(true);
    try {
      const response = await register(registrationData);
      if (!response.accountId) {
        setError({
          message: formatErrorMessage({
            id: 'account.create.fail',
            defaultMessage: "Sorry. We couldn't create your account..",
          }),
          type: 'error',
        });
        setSuccess('');
      } else {
        setError({
          message: formatErrorMessage({
            id: 'account.create.success',
            defaultMessage: 'Successfully Registered!',
          }),
          type: 'success',
        });
        //await login(data.email, data.password);
        setTimeout(() => {
          if (!loggedIn) {
            router.push('/login');
          }
        }, 200);
      }
    } catch (err) {
      setError({
        message: formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }),
        type: 'error',
      });
      setSuccess('');
    }
    setLoading(false);
  };

  const hasCart = cartData?.lineItems ? cartData.lineItems.length > 0 : false;
  if (loggedIn && (hasCart || lvp === 'cart')) return <Redirect target="/cart" />;
  if (loggedIn) return <Redirect target="/account" />;

  return (
    <>
      <Typography
        as="h3"
        className="mb-16 text-center font-heading text-[23px] font-semibold uppercase leading-[25px] md:w-full md:text-[34px] md:leading-[40px] lg:text-[28px]  xl:text-[34px]"
      >
        {formatAccountMessage({ id: 'become.member', defaultMessage: 'Create an account' })}
      </Typography>

      {formStep === 'CHECK_EMAIL' && (
        <form onSubmit={handleEmailSubmit} className="m-auto w-full md:max-w-[480px]" noValidate>
          {error.message && <Message severity={error.type} message={error.message} />}
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="mb-16 md:mb-20"
            placeholder={formatMessage({ id: 'emailAddress', defaultMessage: 'Email Address' })}
            onChange={handleChange}
            showValidationError={false}
          />
          <Button
            size="full"
            type="submit"
            className="mb-16 text-16 leading-tight md:mb-20"
            disabled={loading}
            loading={loading}
          >
            {formatAccountMessage({ id: 'continue', defaultMessage: 'Continue' })}
          </Button>
        </form>
      )}

      {formStep === 'SET_PASSWORD' && (
        <>
          <form onSubmit={handlePullAndRegister} noValidate className="m-auto w-full md:max-w-[480px]">
            {error.message && <Message severity={error.type} message={error.message} />}
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="mb-16 md:mb-20"
              placeholder={formatMessage({ id: 'emailAddress', defaultMessage: 'Email Address' })}
              defaultValue={data?.email}
              value={data.email}
              disabled
              showValidationError={false}
            />
            <PasswordInput
              required
              id="password"
              name="password"
              autoComplete="current-password"
              placeholder={formatAccountMessage({ id: 'password', defaultMessage: 'Password' })}
              className="mb-16 md:mb-20"
              onChange={handleChange}
            />
            <Button
              size="full"
              type="submit"
              className="mb-16 text-16 leading-tight md:mb-20"
              disabled={loading}
              loading={loading}
            >
              {formatAccountMessage({ id: 'sign.in', defaultMessage: 'Sign in' })}
            </Button>
          </form>
        </>
      )}

      {formStep === 'REGISTER' && (
        <>
          {error.message && <Message severity={error.type} message={error.message}></Message>}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
            <div>
              <Dropdown
                name="title"
                value={selectedTitle}
                items={[
                  { label: 'Mr', value: 'Mr' },
                  { label: 'Mrs', value: 'Mrs' },
                  { label: 'Ms', value: 'Ms' },
                  { label: 'Miss', value: 'Miss' },
                  { label: 'Dr', value: 'Dr' },
                ]}
                onChange={handleTitleChange}
                className="mb-16 w-full text-16 font-light md:mb-20"
                label="Title"
                isRequired={true}
              />
            </div>
            <div>
              <Input
                id="email"
                name="email"
                variant="primary"
                type="email"
                autoComplete="email"
                required
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'emailAddress', defaultMessage: 'Email Address' })}
                disabled
                value={data.email}
                showValidationError={false}
              />
            </div>
            <div>
              <Input
                id="name"
                name="firstName"
                variant="primary"
                type="text"
                autoComplete="firstName"
                required
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'firstName', defaultMessage: 'First Name' })}
                onChange={handleChange}
                maxLength={21}
                showValidationError={false}
              />
            </div>
            <div>
              <Input
                id="name"
                name="lastName"
                variant="primary"
                type="text"
                autoComplete="lastName"
                required
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'lastName', defaultMessage: 'Last Name' })}
                onChange={handleChange}
                maxLength={31}
                showValidationError={false}
              />
            </div>
            <div>
              <PasswordInput
                required
                id="password"
                name="password"
                autoComplete="current-password"
                label={formatAccountMessage({ id: 'password', defaultMessage: 'Password' })}
                className={'mb-16 md:mb-20'}
                onChange={handleChange}
              />
            </div>
            <div>
              <PasswordInput
                required
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                label={formatAccountMessage({ id: 'confirmPassword', defaultMessage: 'Confirm Password' })}
                className={'mb-16 md:mb-20'}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            <div className="col-span-2">
              <div className="mb-16">
                <label className="text-sm font-light">Date of birth</label>
              </div>
              <div className="mb-16 grid grid-cols-3 gap-4 md:col-span-1 md:mb-20">
                <Dropdown
                  name="day"
                  value={dob.day}
                  onChange={handleDobChange}
                  className="mb-16 w-full border-neutral-500 text-sm font-light md:mb-20"
                  isRequired={false}
                  items={[
                    { label: 'Day', value: '' },
                    ...Array.from({ length: 31 }, (_, i) => ({ label: `${i + 1}`, value: `${i + 1}` })),
                  ]}
                />
                <Dropdown
                  name="month"
                  value={dob.month}
                  onChange={handleDobChange}
                  className="mb-16 w-full border-neutral-500 text-sm font-light md:mb-20"
                  isRequired={false}
                  items={[
                    { label: 'Month', value: '' },
                    ...Array.from({ length: 12 }, (_, i) => ({ label: `${i + 1}`, value: `${i + 1}` })),
                  ]}
                />
                <Dropdown
                  name="year"
                  value={dob.year}
                  onChange={handleDobChange}
                  className="mb-16 w-full border-neutral-500 text-sm font-light md:mb-20"
                  isRequired={false}
                  items={[
                    { label: 'Year', value: '' },
                    ...years.map((year) => ({ label: `${year}`, value: `${year}` })),
                  ]}
                />
              </div>
            </div>
            <div>
              <Input
                name="line1"
                required={true}
                onChange={handleAddressChange}
                autoComplete="line1"
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'address.line1', defaultMessage: 'Address Line 1' })}
                showValidationError={false}
              />
            </div>
            <div>
              <Input
                name="line2"
                onChange={handleAddressChange}
                autoComplete="line2"
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'address.line2', defaultMessage: 'Address Line 2' })}
                showValidationError={false}
              />
            </div>
            <div>
              <Input
                name="city"
                required={true}
                onChange={handleAddressChange}
                autoComplete="city"
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'address.city', defaultMessage: 'City' })}
                showValidationError={false}
              />
            </div>
            <div>
              <Input
                name="postcode"
                required={true}
                onChange={handleAddressChange}
                autoComplete="postcode"
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'address.postcode', defaultMessage: 'Postcode' })}
                showValidationError={false}
              />
            </div>
            <div>
              <Input
                name="county"
                onChange={handleAddressChange}
                autoComplete="county"
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'address.county', defaultMessage: 'County' })}
                showValidationError={false}
              />
            </div>

            <div>
              <Dropdown
                name="country"
                required={true}
                items={countryDropdownList} // Assume you have a countries list from your uploaded image or other source
                onChange={handleAddressChange}
                className="mb-16 w-full text-16 font-light md:mb-20"
                label={formatMessage({ id: 'address.country', defaultMessage: 'Country' })}
                defaultValue={address.country}
              />
            </div>

            <div className="col-span-2">
              <Input
                name="phone"
                required={true}
                onChange={handleMobileChange}
                autoComplete="phone"
                className={'mb-16 md:mb-20'}
                label={formatMessage({ id: 'phone', defaultMessage: 'Mobile' })}
                showValidationError={false}
              />
            </div>

            <div className="mb-18 flex flex-wrap items-center justify-center gap-4 md:col-span-2">
              <Typography className="text-12 font-light text-secondary-black md:text-14">
                {formatAccountMessage({
                  id: 'by.registering',
                  defaultMessage:
                    'By creating an account you agree to sign up, for free, to the American Golf Club. You can choose your marketing preferences below.',
                })}
              </Typography>
            </div>
            <div className="mb-18 gap-4 md:col-span-2">
              <Typography className="gap-4 text-12 font-medium text-secondary-black md:col-span-2 md:text-14">
                {formatAccountMessage({
                  id: 'marketing.preferences',
                  defaultMessage: 'I would like to receive up to date news, offers and promotions',
                })}
              </Typography>
              {/* Marketing preferences checkboxes */}
              <div className="mt-16 flex flex-col gap-4">
                <div className="mb-8">
                  <Checkbox
                    type="checkbox"
                    name="email"
                    checked={marketingPreferences.email}
                    onChange={handleMarketingPreferenceChange}
                    label="By Email"
                  />
                </div>
                <div className="mb-8">
                  <Checkbox
                    type="checkbox"
                    name="post"
                    checked={marketingPreferences.post}
                    onChange={handleMarketingPreferenceChange}
                    label="By Mail"
                  />
                </div>
                <div className="mb-8">
                  <Checkbox
                    type="checkbox"
                    name="sms"
                    checked={marketingPreferences.sms}
                    onChange={handleMarketingPreferenceChange}
                    label="By SMS"
                  />
                </div>
                <div className="mb-8">
                  <Checkbox
                    type="checkbox"
                    name="phone"
                    checked={marketingPreferences.phone}
                    onChange={handleMarketingPreferenceChange}
                    label="By Phone"
                  />
                </div>
              </div>
            </div>
            <div className="m-auto  w-full md:col-span-2">
              <Button
                size="full"
                type="submit"
                className="mb-16 text-16 leading-tight md:mb-20"
                disabled={loading}
                loading={loading}
              >
                {formatAccountMessage({ id: 'account.register', defaultMessage: 'Register' })}
              </Button>
            </div>
          </form>
        </>
      )}
    </>
  );
};

export default RegisterForm;
